import {
  useEffect,
  useId,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  aiRequestResultSchema,
  type AIAvailabilityResponse,
  type AIRequestFields,
  type AIRequestResult,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Dialog as Modal } from "@primereact/ui/dialog";
import { Textarea } from "@primereact/ui/textarea";
import { usePermissions } from "../../lib/auth.js";
import { apiGet, apiPost, apiUpload } from "../../lib/api.js";
const AI_FIELD_KEYS = [
  "shortDescription",
  "description",
  "categoryId",
  "subcategoryId",
  "pricingPreference",
  "fixedAmount",
  "schedulingPreference",
  "requestedDate",
  "requestedTimeStart",
  "requestedTimeEnd",
  "priority",
] as const satisfies readonly (keyof AIRequestFields)[];
const AUDIO_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;
/** Dictations stop by themselves so they stay well within the upload limit. */
const MAX_RECORDING_SECONDS = 5 * 60;
type AIFieldKey = (typeof AI_FIELD_KEYS)[number];
export type AIRequestFormFields = Record<AIFieldKey, string>;
export type AIRequestChanges = Partial<AIRequestFormFields>;
export type AIRequestValueLabels = Partial<
  Record<AIFieldKey, Record<string, string>>
>;
type CaptureState = "idle" | "requesting" | "recording" | "processing";
type CaptureDialog = "autocomplete" | "dictation" | null;
type ResultSource = "autocomplete" | "dictation";
type PendingReview = {
  changes: AIRequestChanges;
  warnings: string[];
  source: ResultSource;
};
function getSupportedAudioMimeType(): string | null {
  if (
    typeof window === "undefined" ||
    typeof window.MediaRecorder === "undefined" ||
    typeof window.MediaRecorder.isTypeSupported !== "function"
  ) {
    return null;
  }
  return (
    AUDIO_MIME_TYPES.find((mimeType) =>
      window.MediaRecorder.isTypeSupported(mimeType),
    ) ?? null
  );
}
function canCaptureAudio(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    getSupportedAudioMimeType() !== null
  );
}
function stopTracks(streamRef: MutableRefObject<MediaStream | null>): void {
  streamRef.current?.getTracks().forEach((track) => track.stop());
  streamRef.current = null;
}
function audioFileExtension(mimeType: string): string {
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  return "webm";
}
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error";
}
export function AIRequestCapture({
  currentFields,
  valueLabels,
  onApply,
}: {
  currentFields: AIRequestFormFields;
  valueLabels: AIRequestValueLabels;
  onApply: (changes: AIRequestChanges) => void;
}) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canUseAI = has("ai.use");
  const statusId = useId();
  const [captureDialog, setCaptureDialog] = useState<CaptureDialog>(null);
  const [promptText, setPromptText] = useState("");
  const [captureState, setCaptureState] = useState<CaptureState>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [autoCompleting, setAutoCompleting] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [operationMessage, setOperationMessage] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [pendingReview, setPendingReview] = useState<PendingReview | null>(
    null,
  );
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const captureAttemptRef = useRef(0);
  const mountedRef = useRef(true);
  const recordingStartedAtRef = useRef(0);
  const availability = useQuery({
    queryKey: ["ai", "availability"],
    queryFn: () =>
      apiGet<{
        data: AIAvailabilityResponse;
      }>("/ai/availability").then((response) => response.data),
    enabled: canUseAI,
    staleTime: 30_000,
    retry: false,
  });
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      captureAttemptRef.current += 1;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder?.state !== "inactive") recorder?.stop();
      stopTracks(streamRef);
    };
  }, []);
  useEffect(() => {
    if (captureState !== "recording") return;
    const updateElapsed = () => {
      const seconds = Math.max(
        0,
        Math.floor((Date.now() - recordingStartedAtRef.current) / 1000),
      );
      setElapsedSeconds(seconds);
      const recorder = recorderRef.current;
      if (seconds >= MAX_RECORDING_SECONDS && recorder?.state === "recording") {
        setCaptureState("processing");
        recorder.stop();
      }
    };
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 250);
    return () => window.clearInterval(timer);
  }, [captureState]);
  if (
    !canUseAI ||
    availability.isLoading ||
    availability.error ||
    !availability.data?.enabled ||
    !availability.data.allowed
  ) {
    return null;
  }
  const budgetRemaining = Number(availability.data.remainingUsd);
  const aiAvailable = Number.isFinite(budgetRemaining) && budgetRemaining > 0;
  const recordingSupported = canCaptureAudio();
  const busy =
    autoCompleting ||
    captureState === "requesting" ||
    captureState === "processing";
  const fieldLabels: Record<AIFieldKey, string> = {
    shortDescription: t("requests.ai.fields.shortDescription", {
      defaultValue: "Short description",
    }),
    description: t("requests.ai.fields.description", {
      defaultValue: "Detailed description",
    }),
    categoryId: t("requests.ai.fields.category", {
      defaultValue: "Category",
    }),
    subcategoryId: t("requests.ai.fields.subcategory", {
      defaultValue: "Subcategory",
    }),
    pricingPreference: t("requests.ai.fields.pricing", {
      defaultValue: "Pricing expectation",
    }),
    fixedAmount: t("requests.ai.fields.fixedAmount", {
      defaultValue: "Fixed amount",
    }),
    schedulingPreference: t("requests.ai.fields.scheduling", {
      defaultValue: "Scheduling",
    }),
    requestedDate: t("requests.ai.fields.requestedDate", {
      defaultValue: "Desired date",
    }),
    requestedTimeStart: t("requests.ai.fields.startTime", {
      defaultValue: "Preferred start time",
    }),
    requestedTimeEnd: t("requests.ai.fields.endTime", {
      defaultValue: "Preferred end time",
    }),
    priority: t("requests.ai.fields.priority", {
      defaultValue: "Priority",
    }),
  };
  const unavailableReason = !aiAvailable
    ? t("requests.ai.budgetExhausted", {
        defaultValue:
          "The monthly AI budget has been used. You can continue manually.",
      })
    : null;
  const displayValue = (key: AIFieldKey, value: string): string => {
    if (!value) {
      return t("requests.ai.emptyValue", {
        defaultValue: "Empty",
      });
    }
    const label = valueLabels[key]?.[value];
    if (label) return label;
    if (key === "categoryId" || key === "subcategoryId") {
      return t("requests.ai.unknownSelection", {
        defaultValue: "Unavailable selection",
      });
    }
    return value;
  };
  const applyResult = (result: AIRequestResult, source: ResultSource) => {
    const changes: AIRequestChanges = {};
    for (const key of AI_FIELD_KEYS) {
      const proposedValue = result.fields[key];
      if (proposedValue !== null && proposedValue !== currentFields[key]) {
        changes[key] = proposedValue;
      }
    }
    const changedKeys = AI_FIELD_KEYS.filter(
      (key) => changes[key] !== undefined,
    );
    setWarnings(result.warnings);
    if (changedKeys.length === 0) {
      setOperationMessage(
        t("requests.ai.noChanges", {
          defaultValue: "AI did not suggest any changes.",
        }),
      );
      return;
    }
    const overwritesUserInput = changedKeys.some(
      (key) => currentFields[key].trim().length > 0,
    );
    if (overwritesUserInput) {
      setPendingReview({
        changes,
        warnings: result.warnings,
        source,
      });
      setOperationMessage(null);
      return;
    }
    onApply(changes);
    setOperationMessage(
      source === "dictation"
        ? t("requests.ai.dictationApplied", {
            defaultValue:
              "The dictation was summarized into the request. Review the fields before saving.",
          })
        : t("requests.ai.autocompleteApplied", {
            defaultValue:
              "Suggestions were added to the request. Review them before saving.",
          }),
    );
  };
  const runAutocomplete = async () => {
    const prompt = promptText.trim();
    if (prompt.length < 3) return;
    setAutoCompleting(true);
    setOperationError(null);
    setOperationMessage(null);
    setWarnings([]);
    try {
      const response = await apiPost<{
        data: unknown;
      }>("/ai/autocomplete", {
        prompt,
      });
      setCaptureDialog(null);
      setPromptText("");
      applyResult(aiRequestResultSchema.parse(response.data), "autocomplete");
      void availability.refetch();
    } catch (error) {
      setOperationError(
        t("requests.ai.autocompleteError", {
          defaultValue: "The request could not be generated: {{message}}",
          message: errorMessage(error),
        }),
      );
    } finally {
      if (mountedRef.current) setAutoCompleting(false);
    }
  };
  const uploadRecording = async (
    chunks: Blob[],
    mimeType: string,
    attempt: number,
  ) => {
    if (!mountedRef.current || attempt !== captureAttemptRef.current) return;
    setCaptureState("processing");
    try {
      const audio = new Blob(chunks, {
        type: mimeType,
      });
      if (audio.size === 0) {
        throw new Error(
          t("requests.ai.emptyRecording", {
            defaultValue: "No audio was captured.",
          }),
        );
      }
      const form = new FormData();
      form.append("file", audio, `dictation.${audioFileExtension(mimeType)}`);
      const response = await apiUpload<{
        data: unknown;
      }>("/ai/dictation", form);
      setCaptureDialog(null);
      applyResult(aiRequestResultSchema.parse(response.data), "dictation");
      void availability.refetch();
    } catch (error) {
      if (mountedRef.current && attempt === captureAttemptRef.current) {
        setOperationError(
          t("requests.ai.dictationError", {
            defaultValue: "The recording could not be processed: {{message}}",
            message: errorMessage(error),
          }),
        );
      }
    } finally {
      if (mountedRef.current && attempt === captureAttemptRef.current) {
        setCaptureState("idle");
      }
    }
  };
  const startRecording = async () => {
    const mimeType = getSupportedAudioMimeType();
    if (!mimeType) return;
    const attempt = captureAttemptRef.current + 1;
    captureAttemptRef.current = attempt;
    setCaptureState("requesting");
    setOperationError(null);
    setOperationMessage(null);
    setWarnings([]);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      if (!mountedRef.current || attempt !== captureAttemptRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, {
        mimeType,
      });
      recorderRef.current = recorder;
      recorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      });
      recorder.addEventListener(
        "stop",
        () => {
          recorderRef.current = null;
          stopTracks(streamRef);
          if (attempt !== captureAttemptRef.current) return;
          void uploadRecording([...chunksRef.current], mimeType, attempt);
        },
        {
          once: true,
        },
      );
      recordingStartedAtRef.current = Date.now();
      setElapsedSeconds(0);
      recorder.start();
      setCaptureState("recording");
    } catch (error) {
      stopTracks(streamRef);
      recorderRef.current = null;
      if (mountedRef.current && attempt === captureAttemptRef.current) {
        setCaptureState("idle");
        setOperationError(
          t("requests.ai.microphoneError", {
            defaultValue: "The microphone could not be started: {{message}}",
            message: errorMessage(error),
          }),
        );
      }
    }
  };
  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder?.state === "recording") {
      setCaptureState("processing");
      recorder.stop();
    }
  };
  const cancelRecording = () => {
    captureAttemptRef.current += 1;
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder?.state !== "inactive") recorder?.stop();
    stopTracks(streamRef);
    chunksRef.current = [];
    setCaptureState("idle");
    setElapsedSeconds(0);
  };
  const closeCaptureDialog = () => {
    if (autoCompleting || captureState === "processing") return;
    if (captureState === "recording" || captureState === "requesting") {
      cancelRecording();
    }
    setCaptureDialog(null);
  };
  const formatElapsed = (seconds: number) =>
    `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  return (
    <section
      className="mb-4 rounded-xl border border-brand/20 bg-brand/5 p-4 sm:p-5"
      aria-labelledby={`${statusId}-title`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id={`${statusId}-title`} className="font-semibold text-app-text">
            {t("requests.ai.title", {
              defaultValue: "AI-assisted capture",
            })}
          </h2>
          <p className="mt-1 text-sm text-app-muted">
            {t("requests.ai.hint", {
              defaultValue:
                "Describe or dictate the request in a separate workspace, then review the proposed form values.",
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => {
              setOperationError(null);
              setCaptureDialog("dictation");
            }}
            disabled={!aiAvailable || !recordingSupported || busy}
            title={
              !recordingSupported
                ? t("requests.ai.recordingUnsupported", {
                    defaultValue:
                      "Audio recording is not supported by this browser.",
                  })
                : (unavailableReason ?? undefined)
            }
          >
            {t("requests.ai.dictation", {
              defaultValue: "Dictation",
            })}
          </Button>
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={() => {
              setOperationError(null);
              setCaptureDialog("autocomplete");
            }}
            disabled={!aiAvailable || busy}
            title={unavailableReason ?? undefined}
          >
            {t("requests.ai.autocomplete", {
              defaultValue: "Auto-fill",
            })}
          </Button>
        </div>
      </div>
      <div
        id={statusId}
        className="mt-3 text-sm text-app-muted"
        aria-live="polite"
      >
        {unavailableReason}
        {operationMessage ? (
          <span className="block font-medium text-app-text">
            {operationMessage}
          </span>
        ) : null}
      </div>
      {operationError ? (
        <p className="mt-2 text-sm text-danger" role="alert">
          {operationError}
        </p>
      ) : null}
      {warnings.length ? (
        <ul className="mt-2 list-disc pl-5 text-sm text-warning" role="status">
          {warnings.map((warning, index) => (
            <li key={`${warning}-${index}`}>{warning}</li>
          ))}
        </ul>
      ) : null}

      <Modal.Root
        open={captureDialog === "autocomplete"}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value && closeCaptureDialog()
        }
      >
        <Modal.Portal>
          <Modal.Backdrop></Modal.Backdrop>
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {t("requests.ai.autocompleteTitle", {
                    defaultValue: "Describe the customer request",
                  })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times"></Modal.Close>
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <Field
                  label={t("requests.ai.promptLabel", {
                    defaultValue: "Request details",
                  })}
                  hint={t("requests.ai.promptHint", {
                    defaultValue:
                      "Include the requested work, scope, timing, priority and price expectations that were discussed.",
                  })}
                  required
                >
                  <Textarea
                    data-autofocus
                    rows={8}
                    maxLength={12000}
                    value={promptText}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setPromptText(event.target.value)
                    }
                  />
                </Field>
                {operationError ? (
                  <p className="mt-3 text-sm text-danger" role="alert">
                    {operationError}
                  </p>
                ) : null}
              </Modal.Content>
              <Modal.Footer>
                {
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      severity="secondary"
                      disabled={autoCompleting}
                      onClick={closeCaptureDialog}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="button"
                      loading={autoCompleting || undefined}
                      disabled={promptText.trim().length < 3 || !aiAvailable}
                      onClick={() => void runAutocomplete()}
                    >
                      {t("requests.ai.generate", {
                        defaultValue: "Generate request",
                      })}
                    </Button>
                  </>
                }
              </Modal.Footer>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      <Modal.Root
        open={captureDialog === "dictation"}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value && closeCaptureDialog()
        }
      >
        <Modal.Portal>
          <Modal.Backdrop></Modal.Backdrop>
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {t("requests.ai.dictationTitle", {
                    defaultValue: "Dictate the customer request",
                  })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times"></Modal.Close>
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <p className="text-sm text-app-muted">
                  {t("requests.ai.dictationHint", {
                    defaultValue:
                      "Describe all relevant work, scope, timing, priority and pricing details. A professional summary will be generated from the recording.",
                  })}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {captureState === "recording" ? (
                    <>
                      <div
                        className="flex min-w-24 items-center justify-center rounded-lg bg-danger-soft px-3 py-2 font-mono text-sm font-semibold text-danger"
                        role="timer"
                        aria-label={t("requests.ai.recordingTime", {
                          defaultValue: "Recording time {{time}}",
                          time: formatElapsed(elapsedSeconds),
                        })}
                      >
                        {formatElapsed(elapsedSeconds)} /{" "}
                        {formatElapsed(MAX_RECORDING_SECONDS)}
                      </div>
                      <Button type="button" onClick={stopRecording}>
                        {t("requests.ai.stopDictation", {
                          defaultValue: "Stop recording",
                        })}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        severity="secondary"
                        onClick={cancelRecording}
                      >
                        {t("requests.ai.cancelDictation", {
                          defaultValue: "Cancel recording",
                        })}
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      data-autofocus
                      onClick={() => void startRecording()}
                      disabled={!aiAvailable || captureState === "processing"}
                      loading={captureState === "requesting" || undefined}
                    >
                      {captureState === "processing"
                        ? t("requests.ai.processing", {
                            defaultValue: "Processing…",
                          })
                        : t("requests.ai.startDictation", {
                            defaultValue: "Start recording",
                          })}
                    </Button>
                  )}
                </div>
                <p className="mt-3 text-sm text-app-muted" aria-live="polite">
                  {captureState === "requesting"
                    ? t("requests.ai.requestingMicrophone", {
                        defaultValue: "Waiting for microphone permission…",
                      })
                    : captureState === "recording"
                      ? t("requests.ai.recording", {
                          defaultValue: "Recording in progress.",
                        })
                      : captureState === "processing"
                        ? t("requests.ai.processingRecording", {
                            defaultValue:
                              "Transcribing and preparing a professional summary…",
                          })
                        : null}
                </p>
                {operationError ? (
                  <p className="mt-3 text-sm text-danger" role="alert">
                    {operationError}
                  </p>
                ) : null}
              </Modal.Content>
              <Modal.Footer>
                {captureState === "idle" ? (
                  <Button
                    type="button"
                    variant="outlined"
                    severity="secondary"
                    onClick={closeCaptureDialog}
                  >
                    {t("common.close")}
                  </Button>
                ) : undefined}
              </Modal.Footer>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      <Modal.Root
        open={pendingReview !== null}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value && (() => setPendingReview(null))()
        }
        size="large"
      >
        <Modal.Portal>
          <Modal.Backdrop></Modal.Backdrop>
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {t("requests.ai.reviewTitle", {
                    defaultValue: "Review proposed changes",
                  })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times"></Modal.Close>
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <p className="mb-4 text-sm text-app-muted">
                  {t("requests.ai.reviewHint", {
                    defaultValue:
                      "Some suggestions replace information you already entered. Compare the values before applying them.",
                  })}
                </p>
                <dl className="space-y-3">
                  {pendingReview
                    ? AI_FIELD_KEYS.filter(
                        (key) => pendingReview.changes[key] !== undefined,
                      ).map((key) => (
                        <div
                          key={key}
                          className="rounded-lg border border-app-border p-3"
                        >
                          <dt className="text-sm font-semibold text-app-text">
                            {fieldLabels[key]}
                          </dt>
                          <dd className="mt-2 grid gap-3 text-sm sm:grid-cols-2">
                            <div>
                              <span className="block text-xs font-medium uppercase tracking-wide text-app-muted">
                                {t("requests.ai.currentValue", {
                                  defaultValue: "Current",
                                })}
                              </span>
                              <span className="mt-1 block whitespace-pre-wrap break-words text-app-text">
                                {displayValue(key, currentFields[key])}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs font-medium uppercase tracking-wide text-brand">
                                {t("requests.ai.proposedValue", {
                                  defaultValue: "Proposed",
                                })}
                              </span>
                              <span className="mt-1 block whitespace-pre-wrap break-words text-app-text">
                                {displayValue(
                                  key,
                                  pendingReview.changes[key] ?? "",
                                )}
                              </span>
                            </div>
                          </dd>
                        </div>
                      ))
                    : null}
                </dl>
              </Modal.Content>
              <Modal.Footer>
                {
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      severity="secondary"
                      onClick={() => setPendingReview(null)}
                    >
                      {t("requests.ai.keepCurrent", {
                        defaultValue: "Keep current values",
                      })}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (!pendingReview) return;
                        onApply(pendingReview.changes);
                        setWarnings(pendingReview.warnings);
                        setOperationMessage(
                          pendingReview.source === "dictation"
                            ? t("requests.ai.dictationApplied", {
                                defaultValue:
                                  "The dictation was summarized into the request. Review the fields before saving.",
                              })
                            : t("requests.ai.autocompleteApplied", {
                                defaultValue:
                                  "Suggestions were applied. Review the fields before saving.",
                              }),
                        );
                        setPendingReview(null);
                      }}
                    >
                      {t("requests.ai.applyChanges", {
                        defaultValue: "Apply proposed changes",
                      })}
                    </Button>
                  </>
                }
              </Modal.Footer>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </section>
  );
}
